import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from "@angular/material/legacy-dialog";
import { Store } from "@ngrx/store";
import { TWO_FACTOR_AUTHENTICATION_DIALOG_ID, twoFactorAuthVerificationDialogFactory } from "@obg/common.verification";
import { CAFAuthenticatorService } from "@obg/mat.ui.common";
import { DialogLayoutComponent, DialogService, NavbarOptions } from "@obg/mat.ui.core";
import { CommonState, TwoFactorAuthFlow } from "@obg/ngx.common";
import {
	AbstractTranslatableSnackBarService,
	Customer,
	CustomerManagementAction,
	ImageContent,
	MessageType,
	TwoFactorAuthMethod,
} from "@obg/ngx.core";
import { CoreContentState, ImageSelector, TranslationGroup } from "@obg/ngx.core.content";
import { Subscription } from "rxjs";
import { switchMap, tap } from "rxjs/operators";

import { ANNUAL_DATA_VALIDATION_EDIT_DIALOG_ID } from "../annual-data-validation.const";

@Component({
	selector: "obg-m-annual-data-validation-edit",
	templateUrl: "./annual-data-validation-edit.container.html",
	styleUrls: ["./annual-data-validation-edit.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class AnnualDataValidationEditContainer implements OnDestroy {
	@HostBinding("class.obg-m-annual-data-validation-edit") hostCssClass = true;
	@ViewChild("dialog") dialogLayoutComponent!: DialogLayoutComponent;

	private twoFactorDialog: MatDialogRef<any, any> | undefined;
	private data$$: Subscription = new Subscription();

	navbarOptions: NavbarOptions = {
		hideBackButton: true,
		showCloseButton: true,
		preventDefaultBack: false,
	};

	loadingSpinner: boolean | undefined = undefined;
	shouldShow = true;

	constructor(
		private matDialog: MatDialog,
		private snackBarService: AbstractTranslatableSnackBarService,
		private imageSelector: ImageSelector,
		private store: Store<CommonState & CoreContentState>,
		private cafAuthenticatorService: CAFAuthenticatorService,
		private dialogService: DialogService,
		private changeDetector: ChangeDetectorRef,
		private customerManagementAction: CustomerManagementAction
	) {}

	handleCustomerUpdateOutcome(isSuccessful: boolean) {
		this.matDialog.closeAll();
		this.store
			.select(
				this.imageSelector.getByKey("general", isSuccessful ? "general.success-icon" : "general.error-icon")
			)
			.pipe(
				switchMap((icon: ImageContent) =>
					this.snackBarService.openSnackBar$(
						{
							message: isSuccessful
								? "account-details.annual-data-validation.success-message"
								: "account-details.annual-data-validation.failure-message",
							dismissible: true,
							duration: 10000,
							icon,
							type: isSuccessful ? MessageType.Success : MessageType.Error,
						},
						[TranslationGroup.Common]
					)
				)
			)
			.subscribe(); // eslint-disable-line rxjs/no-ignored-subscription
	}

	closeDialog() {
		this.matDialog.openDialogs?.find((dialog) => dialog.id === ANNUAL_DATA_VALIDATION_EDIT_DIALOG_ID)?.close();
	}

	openFacialRecognition() {
		this.shouldShow = false;
		this.loadingSpinner = undefined;
		this.data$$?.add(
			this.cafAuthenticatorService
				.openCafAuthenticator$()
				.pipe(
					tap((auth) => {
						this.shouldShow = true;
						this.loadingSpinner = !!auth;
						this.changeDetector.markForCheck();
					})
				)
				.subscribe()
		);
	}

	openTwoFactorAuthentication(details: { xAuthToken: string; phoneNumber: string }) {
		this.loadingSpinner = true;
		this.shouldShow = false;
		this.twoFactorDialog = this.dialogService.openComponent(
			twoFactorAuthVerificationDialogFactory({
				authFlow: TwoFactorAuthFlow.ChangePhoneFlow,
				forceMethod: TwoFactorAuthMethod.SmsTwoFactorAuthenticationMethod,
				hideRememberDevice: true,
				xAuthToken: details.xAuthToken,
				newPhoneNumber: details.phoneNumber,
			}),
			{
				id: TWO_FACTOR_AUTHENTICATION_DIALOG_ID,
				disableClose: true,
			}
		);
		this.data$$.add(
			this.twoFactorDialog
				.afterClosed()
				.pipe(
					tap((auth) => {
						this.shouldShow = true;
						this.loadingSpinner = !!auth;
						this.changeDetector.markForCheck();
					})
				)
				.subscribe()
		);
	}

	closeTwoFactorAuthentication() {
		if (this.twoFactorDialog) {
			this.twoFactorDialog.close();
		}
	}

	updateCustomer(customer: Customer) {
		this.store.dispatch(this.customerManagementAction.getCustomerSuccess(customer));
	}

	ngOnDestroy(): void {
		this.data$$.unsubscribe();
	}
}
